import React, { createContext, useContext, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { taskAPI } from '../services/taskApi'
import { transformBackendToFrontend } from '../hooks/useDispatchTasks'
import { statusConfig } from '../data/dispatchData'
import { ContainerTask } from '../types/container'
import { TaskDetailModal } from '../components/dispatch/TaskDetailModal'

interface EntityLinkContextValue {
  onEntityClick: (type: string, id: number | string) => void
  navigateToEntity: (type: string, id: number | string) => void
  /**
   * True while the shared task detail modal is on screen. The modal portals to
   * <body> at z-50, so any host that itself sits above z-50 (Container Monitor
   * is z-100) has to step aside while it's open, or the detail it just opened
   * renders underneath it.
   */
  taskModalOpen: boolean
}

const EntityLinkContext = createContext<EntityLinkContextValue | null>(null)

// Rendering outside the provider is legal — plenty of components use these
// links optionally. But it swallows the click, which looks exactly like a
// broken button, so say so out loud instead of failing in silence.
const fallback: EntityLinkContextValue = {
  onEntityClick: (type, id) =>
    console.warn(`[EntityLink] ignored ${type} #${id} — component is rendered outside EntityLinkProvider`),
  navigateToEntity: (type, id) =>
    console.warn(`[EntityLink] ignored ${type} #${id} — component is rendered outside EntityLinkProvider`),
  taskModalOpen: false,
}

export function useEntityLink() {
  return useContext(EntityLinkContext) ?? fallback
}

interface EntityLinkProviderProps {
  children: React.ReactNode
}

export function EntityLinkProvider({ children }: EntityLinkProviderProps) {
  const navigate = useNavigate()

  // Task modal state
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [taskModalData, setTaskModalData] = useState<ContainerTask | null>(null)
  // The modal's Parties tab lists the other tasks on the same order. The page
  // that normally hosts this modal already has the whole grid in memory; opened
  // from a link we have nothing, so fetch just that order's tasks.
  const [siblingTasks, setSiblingTasks] = useState<ContainerTask[]>([])

  const onEntityClick = useCallback(async (type: string, id: number | string) => {
    if (type === 'task') {
      try {
        const numId = typeof id === 'string' ? parseInt(id, 10) : id
        const backendTask = await taskAPI.getById(numId)
        // Use the dispatch grid's own transform, not a trimmed-down copy —
        // every tab in this modal (parties, route, payment) reads fields a
        // partial mapping silently drops, and they render as "--" instead of
        // looking broken.
        const containerTask = transformBackendToFrontend(backendTask)
        setTaskModalData(containerTask)
        setSiblingTasks([containerTask])
        setTaskModalOpen(true)

        if (backendTask.order_id) {
          taskAPI
            .getAll({ order_id: backendTask.order_id, limit: 50 })
            .then(res => setSiblingTasks((res.data || []).map(transformBackendToFrontend)))
            .catch(err => console.error('Failed to load sibling tasks:', err))
        }
      } catch (err) {
        console.error('Failed to load task for modal:', err)
      }
    } else if (type === 'order') {
      // Navigate to order dispatch page — viewOrder opens the detail modal
      navigate(`/dispatch-console/order-dispatch?viewOrder=${id}`)
    }
  }, [navigate])

  const navigateToEntity = useCallback((type: string, id: number | string) => {
    if (type === 'task') {
      navigate(`/dispatch-console/dispatch-tasks?taskId=${id}`)
    } else if (type === 'order') {
      // expandOrder expands the row in the table
      navigate(`/dispatch-console/order-dispatch?expandOrder=${id}`)
    }
  }, [navigate])

  const handleTaskModalClose = useCallback(() => {
    setTaskModalOpen(false)
    setTaskModalData(null)
    setSiblingTasks([])
  }, [])

  const handleTaskModalSave = useCallback(async () => {
    // No-op for now — preview modal is read-only
  }, [])

  return (
    <EntityLinkContext.Provider value={{ onEntityClick, navigateToEntity, taskModalOpen }}>
      {children}
      {taskModalData && (
        <TaskDetailModal
          task={taskModalData}
          open={taskModalOpen}
          onClose={handleTaskModalClose}
          onSave={handleTaskModalSave}
          statusConfig={statusConfig}
          allContainerTasks={siblingTasks}
        />
      )}
    </EntityLinkContext.Provider>
  )
}
